/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Route as ReactRoute,
  RouteComponentProps,
  RouteProps as ReactRouteProps,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { StaticContext } from 'react-router';
import { QueryKey, useQuery } from 'react-query';
import ErrorBoundary from 'components/ErrorBoundary';
import Error404 from 'pages/Error404';
import ViewPet from 'pages/ViewPet';
import EditPet from 'pages/EditPet';
import AllPet from 'pages/AllPet';
import MemorialPage from 'pages/MemorialPage';
import FormPage from 'pages/FormPage/FormPage';
import SubNav from 'components/subnav/SubNav';
import { StatusCodes } from 'http-status-codes';
import { QueryFunction } from 'react-query/types/core/types';
import PuppyGuide from 'pages/PuppyGuide';
import FoodWizardPage from 'pages/FoodWizard';
import ComponentsPage from 'pages/ComponentsPage/ComponentsPage';
import NewPet from 'pages/NewPet';
import MintPage from 'pages/MintPage';
import ManagePet from 'pages/ManagePet';
import AvatarPage from 'pages/AvatarPage/AvatarPage';
import {
  autoshipDeactivatedPetsEnabledLoadData,
  featureConfiguredFormLoadData,
  isAleEnabledFormLoadData,
} from 'api/featuresApi';
import { ClientChewyAnalytics } from 'analytics/ClientChewyAnalytics';
import { handleAuthError, isAuth, isAuthCheckAtCookie } from './util/auth';
import { getPet, getPetWithOwnerName, getActivePets, getInactivePets } from './api/petApi';
import Document from './Document';
import {
  avatarPath,
  formPath,
  foodFinderPath,
  getPetAllPath,
  getPetEditPath,
  getPetManagePath,
  getPetNewPath,
  getPetProfilePath,
  getPetsPath,
  mintPath,
  puppyGuide,
  getPetMemorialPath,
  componentsPath,
  IS_ALE_ENABLED,
  ENV,
} from './util/config';
import { isNotChewyMobileApp } from './util/helpers';

interface LoadData extends QueryFunction {
  key: QueryKey;
  requiresPetId: boolean;
}

const LEGACY_ROUTE = '/app/account';

export type RouteProps = {
  // While the `loadData` convention is suggested by the React Router docs, it isn't actually typed on `ReactRouteProps`
  loadDataList?: Array<LoadData>;
  // For our use case, these are not optional
  path: string;
  context?: StaticContext;
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  hasHeaderAndFooter: boolean;
  usesMinimalHeader?: boolean;
  hasSubNav: boolean;
  componentClass?: string;
  disableHeaderAndFooterOnMobile?: boolean;
  authRequired: boolean;
} & ReactRouteProps;

const spaRoutes: RouteProps[] = [
  {
    path: getPetProfilePath(),
    exact: false,
    component: ViewPet,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    hasSubNav: false,
    authRequired: true,
    loadDataList: [getPet],
  },
  {
    path: getPetEditPath(),
    exact: false,
    component: EditPet,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    hasSubNav: false,
    authRequired: true,
    loadDataList: [getPet, featureConfiguredFormLoadData],
  },
  {
    path: getPetNewPath(),
    exact: true,
    hasSubNav: false,
    component: NewPet,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    authRequired: true,
    loadDataList: [featureConfiguredFormLoadData],
  },
  {
    path: getPetManagePath(),
    exact: false,
    component: ManagePet,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    usesMinimalHeader: true,
    hasSubNav: false,
    authRequired: true,
    loadDataList: [autoshipDeactivatedPetsEnabledLoadData, getPetWithOwnerName],
    componentClass: 'manage-pet-container',
  },
  {
    path: puppyGuide,
    exact: true,
    hasSubNav: false,
    component: PuppyGuide,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    authRequired: false,
    componentClass: 'puppy-guide-layout-container',
  },
  {
    path: foodFinderPath,
    exact: true,
    hasSubNav: false,
    component: FoodWizardPage,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    authRequired: false,
  },
  {
    path: mintPath,
    exact: true,
    hasSubNav: false,
    component: MintPage,
    hasHeaderAndFooter: false,
    disableHeaderAndFooterOnMobile: true,
    authRequired: false,
  },
  {
    path: componentsPath,
    exact: true,
    hasSubNav: false,
    component: ComponentsPage,
    hasHeaderAndFooter: false,
    disableHeaderAndFooterOnMobile: true,
    authRequired: false,
  },
  {
    path: getPetAllPath(),
    exact: true,
    hasSubNav: true,
    component: AllPet,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    loadDataList: [getActivePets, getInactivePets],
    authRequired: true,
    componentClass: 'all-pets-container',
  },
  {
    path: getPetsPath(),
    exact: true,
    hasSubNav: true,
    component: AllPet,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    loadDataList: [getActivePets],
    authRequired: true,
    componentClass: 'all-pets-container',
  },
  {
    path: getPetMemorialPath(),
    exact: false,
    component: MemorialPage,
    hasHeaderAndFooter: true,
    disableHeaderAndFooterOnMobile: true,
    usesMinimalHeader: true,
    hasSubNav: false,
    authRequired: true,
    loadDataList: [getPet],
    componentClass: 'memorial-page-container',
  },
  {
    path: formPath,
    exact: true,
    hasSubNav: false,
    component: FormPage,
    hasHeaderAndFooter: false,
    disableHeaderAndFooterOnMobile: true,
    authRequired: true,
  },
  {
    path: avatarPath,
    exact: true,
    hasSubNav: false,
    component: AvatarPage,
    hasHeaderAndFooter: false,
    disableHeaderAndFooterOnMobile: true,
    loadDataList: [getActivePets],
    authRequired: true,
  },
];

const legacyRoutes = spaRoutes.map((route) => ({ ...route, path: `${LEGACY_ROUTE}${route.path}` }));
export const routes = [...legacyRoutes, ...spaRoutes];

/**
 * Wrap each page component in it's own ErrorBoundary
 */
const Route = ({
  component: Component,
  path,
  authRequired,
  componentClass,
  hasSubNav,
  ...other
}: RouteProps): JSX.Element => {
  const { data: featureFlagData } = useQuery(
    isAleEnabledFormLoadData.key,
    isAleEnabledFormLoadData,
  );

  return (
    <ReactRoute
      render={(props: RouteComponentProps<any>) => {
        const isAleEnabled = featureFlagData?.[IS_ALE_ENABLED]?.toLowerCase() === 'true';
/////////////////////////
        // solution reference: https://stackoverflow.com/questions/34836500/how-to-set-up-google-analytics-for-react-router
        if (typeof window.ga === 'function') {
          window.ga('set', 'page', props.location.pathname + props.location.search);
          window.ga('send', 'pageview');
        }
        if (authRequired && !isAuth()) {
          handleAuthError(StatusCodes.UNAUTHORIZED, props.location.pathname);
        }
//////////////////

////////////////////////
///////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////
/////////
//////////////////

        const content = (
          <ErrorBoundary staticContext={props.staticContext}>
            <Document>
              {isNotChewyMobileApp() ? hasSubNav && <SubNav /> : null}
              <div
                id="page-content"
                tabIndex={-1}
                className={
                  componentClass
                    ? `page-content chewy-pet-profile-section ${componentClass}`
                    : 'page-content chewy-pet-profile-section'
                }
              >
                <Component {...props} />
              </div>
            </Document>
          </ErrorBoundary>
        );
        return (
          <>
            {isAleEnabled ? (
              <ClientChewyAnalytics
                version={process.env.DD_VERSION || '1.2.0'}
                region={process.env.EKS_REGION || ''}
                environment={ENV === 'local' ? 'qat' : ENV || 'qat'}
              >
                {content}
              </ClientChewyAnalytics>
            ) : (
              <>{content}</>
            )}
          </>
        );
      }}
      path={path}
      {...other}
    />
  );
};

const Routes = () => {
  const location = useLocation();
  const history = useHistory();
  const currentPath = location.pathname.toLowerCase();

///////////////////
////////////

  if (currentPath.includes(LEGACY_ROUTE)) {
    const newPath = currentPath.replace(LEGACY_ROUTE, '');
    const newLocation = { ...location, pathname: newPath };
    history.replace(newLocation);
    return null;
  }

  return (
    <Switch>
      {routes.map((route) => (
        <Route
          key={route.path}
          authRequired={route.authRequired}
          hasSubNav={route.hasSubNav}
          exact={!!route.exact}
          path={route.path}
          component={route.component}
          hasHeaderAndFooter={route.hasHeaderAndFooter}
          usesMinimalHeader={route.usesMinimalHeader}
          loadDataList={route.loadDataList}
          componentClass={route.componentClass}
        />
      ))}
      <ReactRoute component={Error404} />
    </Switch>
  );
};

export default Routes;
